import React from 'react';
import {
    Autocomplete,
    TextField,
    FormControlLabel,
    Switch,
    Box,
    Chip,
    CircularProgress,
    Typography,
} from '@mui/material';
import {
    PhoneAndroid as PhoneAndroidIcon,
} from '@mui/icons-material';
import type { Uzivatel } from '../api/JsdhNotifikaceService/generated';

interface UserSelectorProps {
    users: Uzivatel[];
    isLoading?: boolean;
    selectedUserIds: number[] | null;  // null = všem
    onSelectionChange: (userIds: number[] | null) => void;
    disabled?: boolean;
}

/**
 * Komponenta pro výběr příjemců notifikací.
 * Obsahuje switch "Odeslat všem" a autocomplete pro výběr konkrétních uživatelů.
 * Zobrazuje pouze aktivní uživatele s povolenými notifikacemi.
 */
const UserSelector: React.FC<UserSelectorProps> = ({
    users,
    isLoading = false,
    selectedUserIds,
    onSelectionChange,
    disabled = false,
}) => {
    // Filtrovat pouze aktivní uživatele s povolenými notifikacemi
    const eligibleUsers = users.filter(
        (u) => u.aktivni && u.notifikaceEnabled
    );

    // Zda je vybrán režim "všem"
    const sendToAll = selectedUserIds === null;

    // Vybraní uživatelé
    const selectedUsers = sendToAll
        ? []
        : eligibleUsers.filter((u) => selectedUserIds?.includes(u.id));

    const handleSendToAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            onSelectionChange(null);
        } else {
            onSelectionChange([]);
        }
    };

    const handleUserSelectionChange = (_: React.SyntheticEvent, value: Uzivatel[]) => {
        onSelectionChange(value.map((u) => u.id));
    };

    return (
        <Box>
            <FormControlLabel
                control={
                    <Switch
                        checked={sendToAll}
                        onChange={handleSendToAllChange}
                        disabled={disabled}
                    />
                }
                label="Odeslat všem aktivním členům"
            />

            {!sendToAll && (
                <Box mt={1}>
                    <Autocomplete
                        multiple
                        options={eligibleUsers}
                        value={selectedUsers}
                        onChange={handleUserSelectionChange}
                        loading={isLoading}
                        disabled={disabled}
                        getOptionLabel={(option) => option.jmeno}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Vybrat členy"
                                placeholder="Začněte psát jméno..."
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                    <Typography>{option.jmeno}</Typography>
                                    <Box display="flex" gap={0.5}>
                                        {(option.validFcmTokenCount ?? 0) > 0 && (
                                            <PhoneAndroidIcon
                                                fontSize="small"
                                                color="primary"
                                                titleAccess={`FCM (${option.validFcmTokenCount} zařízení)`}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </li>
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...rest } = getTagProps({ index });
                                return (
                                    <Chip
                                        key={key}
                                        label={option.jmeno}
                                        size="small"
                                        {...rest}
                                    />
                                );
                            })
                        }
                        noOptionsText="Žádní uživatelé s povolenými notifikacemi"
                        loadingText="Načítám..."
                    />
                    {selectedUsers.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Vyberte alespoň jednoho člena
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default UserSelector;
